//IMPORT - THIRD PARTY
import DOMPurify from "dompurify";
import { Arrive } from "arrive";

//IMPORT - UTILS
import { isJavascriptEnabled } from "./utils/isJavascriptEnabled";
import { getFocusable } from "./utils/getFocusable";
import { nextFocusable } from "./utils/nextFocusable";
import { isPrintableChar } from "./utils/isPrintableChar";
import { isInputable } from "./utils/isInputable";
import { isSet } from "./utils/isSet";

//IMPORT - FUNCTIONS
import { initEmblaCarousels } from "./functions/emblaCarouselInit";
import { apply_utm, clear_utm } from "./functions/utm";

//IMPORT - CLASSES
import { Layer } from "./classes/Layer";
import { Accordion } from "./classes/Accordion";
import { Dropdown } from "./classes/Dropdown";
import { TabList } from "./classes/TabList";
import { StickyHeader } from "./classes/StickyHeader";
import { Table } from "./classes/Table";
import { Masonry } from "./classes/Masonry";
import { Share } from "./classes/Share";
import { NiceSelect } from "./classes/NiceSelect";
import { SiteSearch } from "./classes/SiteSearch";
import { ContentNav } from "./classes/ContentNav";
import { TechSpecTable } from "./classes/TechSpecTable";
import { CoffeeCard } from "./classes/CoffeeCard";
import { MachineFinder } from "./classes/MachineFinder";
import { PhoneSelector } from "./classes/PhoneSelector";
import { FloatingCTA } from "./classes/FloatingCTA";
import { FluidSlider } from "./classes/Slider";

isJavascriptEnabled();

const focusableElements = [
  "a[href]:not([disabled]):not([hidden]):not([aria-hidden])",
  "area[href]:not([disabled]):not([hidden]):not([aria-hidden])",
  'input:not([disabled]):not([type="hidden"]):not([aria-hidden])',
  "select:not([disabled]):not([aria-hidden])",
  "textarea:not([disabled]):not([aria-hidden])",
  "button:not([disabled]):not([aria-hidden])",
  "iframe:not([disabled]):not([hidden]):not([aria-hidden])",
  "object:not([disabled]):not([hidden]):not([aria-hidden])",
  "embed:not([disabled]):not([hidden]):not([aria-hidden])",
  "[contenteditable]:not([disabled]):not([hidden]):not([aria-hidden])",
  "[tabindex]",
];
window.focusableElements = focusableElements;

const store = {
  activeLayers: [],
  activeLayer: "",
};
window.store = store;

function CookieConsentCallback_OnAccept() {
  if (Cookiebot.consent.statistics && Cookiebot.consent.marketing) {
    apply_utm(true);
  } else {
    clear_utm();
    apply_utm(false);
  }
}

function CookieConsentCallback_OnDecline() {
  clear_utm();
  apply_utm(false);
}

window.CookieConsentCallback_OnAccept = CookieConsentCallback_OnAccept;
window.CookieConsentCallback_OnDecline = CookieConsentCallback_OnDecline;

apply_utm(false);

window.addEventListener("ucEvent", function (e) {
  if (!e.detail || e.detail.event !== "consent_status") return;

  if (e.detail["Google Ads"] === true) {
    apply_utm(true);
  } else {
    clear_utm();
    apply_utm(false);
  }
});

window.addEventListener("CookiebotOnAccept", function (e) {
  if (Cookiebot.consent.marketing) {
    apply_utm(true);
  }
});

window.addEventListener(
  "CookiebotOnDecline",
  function (e) {
    clear_utm();
    apply_utm(false);
  },
  false,
);

// STICKY MASTHEAD
/** @type {<Element>} */
const siteHeader = document.querySelector(".site-header.sticky--js");
if (isSet(siteHeader)) {
  const stickyHeader = new StickyHeader(siteHeader);
}

// MACHINE FINDER
/** @type {<Element>} */

const machineFinderElm = document.querySelector(".machine-finder--js");
if (isSet(machineFinderElm)) {
  const machineFinder = new MachineFinder(machineFinderElm);
}


// RTE TABLES
/** @type {NodeListOf<Element>} */
const tableElms = document.querySelectorAll(".table--js");
tableElms.forEach(function (tableElm) {
  new Table(tableElm);
});

/** @type {NodeListOf<Element>} */
const sliderElms = document.querySelectorAll(".component--slider--js");
sliderElms.forEach(function (sliderElm) {
  new FluidSlider(sliderElm, {
    bindTargets: [
      ".slider-input",
      ".slider-count-display--js",
      ".step-field--2",
    ],
  });
});

// SITE HEADER SUB MENUS
/** @type {NodeListOf<Element>} */
const subMenuElms = document.querySelectorAll(".sub-menu--js");
const subMenus = [];
subMenuElms.forEach(function (subMenuElm) {
  const subMenu = new Dropdown(subMenuElm, {
    openOnTriggerHover: true,
    openOnTriggerClick: false,
    onAfterOpen: (obj, e) => {
      subMenus.forEach((subMenu) => {
        if (!isSet(subMenu) && !isSet(obj)) return;
        if (subMenu.dropdown.id === obj.dropdown.id) return;

        subMenu.closeDropdown();
      });
    },
  });

  subMenus.push(subMenu);
});

// SITE HEADER SECONDARY MENU
/** @type {NodeListOf<Element>} */
const secondaryMenuElms = document.querySelectorAll(".secondary-menu--js");
const secondaryMenus = [];
secondaryMenuElms.forEach(function (secondaryMenuElm) {
  const secondaryMenu = new Dropdown(secondaryMenuElm, {
    onAfterOpen: (obj, e) => {
      secondaryMenus.forEach((secondaryMenu) => {
        if (!isSet(secondaryMenu) && !isSet(obj)) return;
        if (secondaryMenu.dropdown.id === obj.dropdown.id) return;

        secondaryMenu.closeDropdown();
      });
    },
  });

  secondaryMenus.push(secondaryMenu);

  /** @type {NodeListOf<Element>} */
  secondaryMenuAccordionElms =
    secondaryMenuElm.querySelectorAll(".ma--accordion--js");
  secondaryMenuElms.forEach(function (secondaryMenuAccordionElm) {
    const secondaryMenuAccordion = new Accordion(secondaryMenuAccordionElm, {
      type: "multiple",
      collapsible: true,
    });
  });
});

// CONTENT NAV
/** @type {NodeListOf<Element>} */
const contentNavElm = document.querySelector("#content-nav");
if (isSet(contentNavElm)) {
  const contentNav = new ContentNav(contentNavElm);
}

// CAROUSELS
initEmblaCarousels();

//LAYERS
const layers = document.querySelectorAll(".layer--js");
layers.forEach(function (layerElm) {
  let layer = new Layer(layerElm, {
    onBeforeClose: (obj, e) => {
      const videos = obj.domNode.querySelectorAll("video");
      videos.forEach((video) => video.pause());
    },
    awaitCloseAnimation: true,
    awaitOpenAnimation: true,
  });
});

document.arrive(".layer--js", (newLayer) => {
  let layer = new Layer(newLayer, {
    onBeforeClose: (obj, e) => {
      const videos = obj.domNode.querySelectorAll("video");
      videos.forEach((video) => video.pause());
    },
    awaitCloseAnimation: true,
    awaitOpenAnimation: true,
  });
});

//MOBILE MENU ACCORDION
const mobileNavElm = document.querySelector("#site-mobile-nav");
if (isSet(mobileNavElm)) {
  const mobileMenuAccordionElm =
    mobileNavElm.querySelector(".ma--accordion--js");
  if (isSet(mobileMenuAccordionElm)) {
    const mobileMenuAccordion = new Accordion(mobileMenuAccordionElm, {
      type: "multiple",
      collapsible: true,
    });
  }
}



/** @type {NodeListOf<Element>} */
const faqAccordionElms = document.querySelectorAll(".faq-accordion--js");
faqAccordionElms.forEach(function (faqAccordionElm) {
    const faqAccordion = new Accordion(faqAccordionElm, {
        type: "multiple",
        collapsible: true,
    });
});

//CLEAN WYSIWYG CONTENT
const rteContentElms = document.querySelectorAll(".rte--js");

const ALLOWED_TAGS__FULL_RTE = [
  "a",
  "address",
  "b",
  "br",
  "del",
  "em",
  "figure",
  "figcaption",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "hr",
  "i",
  "img",
  "mark",
  "ol",
  "ul",
  "li",
  "p",
  "picture",
  "q",
  "s",
  "span",
  "small",
  "sub",
  "sup",
  "u",
  "video",
  "wbr",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "tr",
  "strong",
  "caption",
  "legend",
  "colgroup",
  "col",
];

const ALLOWED_ATTR__FULL_RTE = [
  "src",
  "loading",
  "alt",
  "width",
  "height",
  "scope",
  "colspan",
  "rowspan",
  "span",
  "href",
  "title",
];

rteContentElms.forEach(function (rteContentElm) {
  const dirtyHTML = rteContentElm.innerHTML;
  const cleanHTML = DOMPurify.sanitize(dirtyHTML, {
    ALLOWED_TAGS: ALLOWED_TAGS__FULL_RTE,
    ALLOWED_ATTR: ALLOWED_ATTR__FULL_RTE,
  });
  rteContentElm.innerHTML = cleanHTML;
});

const rteExcerptElms = document.querySelectorAll(".excerpt--js");

const ALLOWED_TAGS__EXCERPT_RTE = [
  "a",
  "b",
  "br",
  "p",
  "span",
  "ol",
  "ul",
  "li",
  "em",
  "mark",
  "i",
];

const ALLOWED_ATTR__EXCERPT_RTE = ["href", "title"];

rteExcerptElms.forEach(function (rteExcerptElm) {
  const dirtyHTML = rteExcerptElm.innerHTML;
  const cleanHTML = DOMPurify.sanitize(dirtyHTML, {
    ALLOWED_TAGS: ALLOWED_TAGS__EXCERPT_RTE,
    ALLOWED_ATTR: ALLOWED_ATTR__EXCERPT_RTE,
  });
  rteExcerptElm.innerHTML = cleanHTML;
});

// MASONRY GRIDS
const masonryGrids = document.querySelectorAll(".masonry--js");
masonryGrids.forEach(function (masonryGridElm) {
  const masonryGrid = new Masonry(masonryGridElm);

  document.arrive("[masonry-item]", (masonryItemElm) => {
    masonryGrid.update();
  });
});

//SHARE COMPONENT
const shareElms = document.querySelectorAll(".share--js");
shareElms.forEach(function (shareElm) {
  let share = new Share(shareElm, {});
});

// SELECT ELEMENTS
const selectElms = document.querySelectorAll(".select--js");
selectElms.forEach(function (selectElm) {
  new NiceSelect(selectElm, {
    searchable: false,
  });
});

// TABLIST ELEMENTS
const tabListElms = document.querySelectorAll(".tablist--js");
tabListElms.forEach(function (tabListElm) {
  new TabList(tabListElm, {
    convertToAccordion: true,
  });
});

// SITE SEARCH
const siteSearchElm = document.querySelector(".site-search--js");
if (isSet(siteSearchElm)) {
  const siteSearch = new SiteSearch(siteSearchElm);
}

// Tech Spec TABLES
/** @type {NodeListOf<Element>} */
const techSpecTableElms = document.querySelectorAll(".tech-spec-table--js");
techSpecTableElms.forEach(function (techSpecTableElm) {
  new TechSpecTable(techSpecTableElm);
});

// Coffee Cards
/** @type {NodeListOf<Element>} */
const coffeeCardElms = document.querySelectorAll(".card--coffee--js");
coffeeCardElms.forEach(function (coffeeCardElm) {
  new CoffeeCard(coffeeCardElm);
});

// Phone Selectors
/** @type {NodeListOf<Element>} */
const phoneSelectorElms = document.querySelectorAll(".phone-selector--js");
phoneSelectorElms.forEach(function (phoneSelectorElm) {
  new PhoneSelector(phoneSelectorElm);
});

// FLoating CTA
/** @type {NodeListOf<Element>} */
const floatingCTAElm = document.querySelector(".floating-cta--js");
if (isSet(floatingCTAElm)) {
  new FloatingCTA(floatingCTAElm);
}

//Course Cards
const courseContainers = document.querySelectorAll(".block--courses");

courseContainers.forEach((container) => {
  const buttons = container.querySelectorAll(".card button[data-id]");
  buttons.forEach((button) => {
    button.addEventListener("click", () => {
      const id = button.getAttribute("data-id");
      const card = button.closest(".card");
      const courseSection = container.querySelector(
        `.course--section[data-id="${id}"]`,
      );

      if (!courseSection || !card) return;

      if (courseSection.classList.contains("is-visible")) {
        courseSection.classList.remove("is-visible");
        card.classList.remove("is-active");
      } else {
        const allSections = container.querySelectorAll(
          ".course--section.is-visible",
        );
        allSections.forEach((section) =>
          section.classList.remove("is-visible"),
        );

        const allCards = container.querySelectorAll(".card.is-active");
        allCards.forEach((c) => c.classList.remove("is-active"));

        courseSection.classList.add("is-visible");
        card.classList.add("is-active");
      }
    });
  });

  const closeButtons = container.querySelectorAll(".course--section button");
  closeButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const section = button.closest(".course--section");
      if (section) {
        section.classList.remove("is-visible");
      }

      const id = section?.getAttribute("data-id");
      if (id) {
        const card = container
          .querySelector(`.card button[data-id="${id}"]`)
          ?.closest(".card");
        if (card) card.classList.remove("is-active");
      }
    });
  });

  const bookingButtons = container.querySelectorAll(
    ".course--section .course__booking button",
  );

  bookingButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const selectValue = button?.getAttribute("data-select-value");

      if (!selectValue) return;

      const selectElm = document.querySelector(".chosencourse select");
      if (!selectElm) return;

      const blockElm = selectElm.closest(".block");

      if (!blockElm) return;

      blockElm.scrollIntoView(true);

      selectElm.value = selectValue;
    });
  });
});
